import { ChangeEvent, KeyboardEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UPButton from 'components/button';
import UPInput from 'components/input';
import {
  MFA_CHALLENGE__BACK_TO_SIGN_IN,
  MFA_CHALLENGE__CODE_LABEL,
  MFA_CHALLENGE__INSTRUCTIONS,
  MFA_CHALLENGE__RETRY_IN_SECONDS,
  MFA_CHALLENGE__TITLE,
  MFA_CHALLENGE__USE_BACKUP_CODE,
  MFA_CHALLENGE__VERIFY,
} from 'translations/constants';
import { MfaFailure, isCompleteCode, normaliseCode, translateMfaFailure } from '../mfa-helpers';
import { MfaButtonRow, MfaCardTitle, MfaErrorSlot, MfaInstructions } from '../mfa-styles';
import { MfaBackupCodeAction } from './styles';

interface MfaTotpViewProps {
  submitting: boolean;
  failure: MfaFailure;
  retrySeconds: number;
  onSubmit: (code: string) => void;
  onBackToLogin: () => void;
  /** Swaps this view for the backup-code one. Same challenge, same token — see `MfaChallenge`. */
  onUseBackupCode: () => void;
  onCodeEdited: () => void;
}

const ENTER_KEY_CODE = 13;

/** The default challenge view: the six-digit code from the user's authenticator app. */
const MfaTotpView = (props: MfaTotpViewProps) => {
  const { submitting, failure, retrySeconds, onSubmit, onBackToLogin, onUseBackupCode, onCodeEdited } = props;
  const [t] = useTranslation();
  const [code, setCode] = useState('');

  const handleCodeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setCode(normaliseCode(event.target.value));
      // Clear the previous failure as soon as the user starts fixing it, so a stale "that code isn't
      // correct" doesn't sit under a field they have already changed.
      onCodeEdited();
    },
    [onCodeEdited],
  );

  const waiting = retrySeconds > 0;
  const canSubmit = !submitting && !waiting && isCompleteCode(code);

  const onKeyPress = useCallback(
    (event: KeyboardEvent<HTMLInputElement>): void => {
      const keyCode = event.keyCode || event.which;
      if (keyCode === ENTER_KEY_CODE && canSubmit) {
        onSubmit(code);
      }
    },
    [canSubmit, onSubmit, code],
  );

  return (
    <>
      <MfaCardTitle>{t(MFA_CHALLENGE__TITLE)}</MfaCardTitle>
      <MfaInstructions>{t(MFA_CHALLENGE__INSTRUCTIONS)}</MfaInstructions>
      {/* No maxLength: it caps the *raw* value in the DOM before onChange runs, so pasting
          "123a456" would arrive here already truncated to "123a45" and normalise to five digits.
          The controlled value plus normaliseCode enforces the six-digit cap instead. */}
      <UPInput
        value={code}
        onChange={handleCodeChange}
        onKeyPress={onKeyPress}
        label={t(MFA_CHALLENGE__CODE_LABEL)}
        placeholder={t(MFA_CHALLENGE__CODE_LABEL)}
        error={!!failure}
        inputMode="numeric"
        autoComplete="one-time-code"
      />
      <MfaErrorSlot>{failure && translateMfaFailure(t, failure)}</MfaErrorSlot>
      <MfaButtonRow>
        <UPButton text={t(MFA_CHALLENGE__BACK_TO_SIGN_IN)} onClick={onBackToLogin} outlined />
        <UPButton
          text={waiting ? t(MFA_CHALLENGE__RETRY_IN_SECONDS, { seconds: retrySeconds }) : t(MFA_CHALLENGE__VERIFY)}
          onClick={() => onSubmit(code)}
          disabled={!canSubmit}
        />
      </MfaButtonRow>
      {/* Not disabled while a request is in flight: switching does not submit anything, and a user
          who has just realised they cannot reach their authenticator should not have to wait for a
          doomed attempt to come back before they can go looking for their printout. */}
      <MfaBackupCodeAction type="button" onClick={onUseBackupCode}>
        {t(MFA_CHALLENGE__USE_BACKUP_CODE)}
      </MfaBackupCodeAction>
    </>
  );
};

export default MfaTotpView;
